// ezp_booking_status holds industry-specific rows (industry = an id, e.g.
// Car Service's own Booked/Checked In/In Progress/.../Ready for Delivery)
// alongside generic rows (industry IS NULL, e.g. Booked/Cancelled/Completed).
// A vendor should only ever see ONE of those sets: their own industry's rows
// if any exist, otherwise the generic ones - never the whole table mixed
// across every industry.
const getStatusIndustry = (status) => {
  // Distinguish "field present and null" (generic row) from "field absent"
  // (a response shape that doesn't carry industry at all) - a `??` chain
  // would treat both the same and silently drop every generic row.
  if (status?.industry !== undefined) return status.industry;
  if (status?.industry_id !== undefined) return status.industry_id;
  return status?.industryId;
};

export const filterStatusesByIndustry = (statuses, expectedIndustry) => {
  if (!Array.isArray(statuses)) return [];

  const rowsWithIndustry = statuses.filter(
    (status) => getStatusIndustry(status) !== undefined
  );
  // Some API responses already contain only the requested rows and do not
  // carry an industry column at all - keep those rows unchanged.
  if (rowsWithIndustry.length === 0) return statuses;

  return rowsWithIndustry.filter((status) => {
    const statusIndustry = getStatusIndustry(status);
    return expectedIndustry === null
      ? statusIndustry === null || statusIndustry === ""
      : String(statusIndustry) === String(expectedIndustry);
  });
};

// apicall: the module's get() function, passed in rather than imported so
// this stays usable from any screen without hard-coding the endpoint host.
export const fetchBookingStatusesForIndustry = async (apiGet, industry) => {
  const hasIndustry = industry !== undefined && industry !== null && industry !== "";

  if (hasIndustry) {
    const industryResponse = await apiGet(
      `get_ezp_booking_status_by_industry?industry=${encodeURIComponent(industry)}`
    );
    const industryStatuses = filterStatusesByIndustry(industryResponse?.data, industry);
    if (industryResponse?.success && industryStatuses.length > 0) {
      return industryStatuses;
    }
  }

  // No industry, or no status configured for this industry - generic
  // (industry IS NULL) rows only.
  const response = await apiGet("get_ezp_booking_status");
  return response?.success ? filterStatusesByIndustry(response.data, null) : [];
};

// A booking can be invoiced once it is completed. ezp_booking_status ids differ per
// vertical, so completion is matched by name - the same list the API uses when it
// refuses to invoice anything else (vendor/invoicePdf.js COMPLETED_STATUS_NAMES).
const COMPLETED_STATUS_NAMES = ["completed", "complete", "check-out", "checked out"];

export const isCompletedStatusName = (name) =>
  COMPLETED_STATUS_NAMES.includes(String(name || "").trim().toLowerCase());
